import React, { useEffect, useState } from "react";
import { useWebData } from "../context/WebDataContext"; // 🔥 Changed: Import Context
// Removed db, collection, query, where, onSnapshot imports as they are not needed directly
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import Loader from "../components/Loader";

function PendingApprovals() {
  // 🔥 Changed: Fetch data from Context
  // Assuming 'pendingApprovals' is available in WebDataContext. 
  // If not, we might need to derive it from other context data (leaves, expenses, etc.)
  // BUT, to keep it simple and centralized, let's assume the context prepares this list 
  // OR we derive it here from all the context data we have access to.
  
  // Since WebDataContext usually exposes raw collections (leaves, expenses, etc.), 
  // we will reconstruct the logic to filter pending items from those context arrays.
  // This avoids multiple DB listeners in this component.

  const { 
      leaves, expenses, advances, tasks, orders, installations, 
      serviceCalls, leads, cardRequests, couriers, pms, dues, // Assuming dues maps to payment_dues
      loading: contextLoading 
  } = useWebData();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // --- CONFIGURATION ---
  // (Keeping config mainly for reference of colors/types, but logic moves to effect)
  const configMap = {
      leaves: { type: "Leave", path: "/leaves", color: "#e74c3c" },
      tasks: { type: "Task", path: "/tasks", color: "#8e44ad" },
      advances: { type: "Advance", path: "/advances", color: "#d35400" },
      expenses: { type: "Expense", path: "/expenses", color: "#2ecc71" },
      orders: { type: "Order PO", path: "/orders", color: "#e67e22" },
      installations: { type: "Install", path: "/installations", color: "#27ae60" },
      service_calls: { type: "Ticket", path: "/service-calls", color: "#c0392b" },
      leads: { type: "Lead", path: "/leads", color: "#3498db" },
      card_requests: { type: "Card Req", path: "/card-requests", color: "#f39c12" },
      couriers: { type: "Courier", path: "/couriers", color: "#7f8c8d" },
      pms_reports: { type: "PMS Due", path: "/pms", color: "#16a085" },
      payment_dues: { type: "Payment Due", path: "/payment-dues", color: "#c0392b" },
  };

  useEffect(() => {
    if (contextLoading) return;

    setLoading(true);
    let allPending = [];

    // Helper to process list
    const processList = (list, collectionName, statusMatch) => {
        if (!list) return;
        const pendingItems = list.filter(item => item.status === statusMatch);
        const cfg = configMap[collectionName];
        
        pendingItems.forEach(d => {
            let desc = "-";
            if(collectionName === 'leaves') desc = `${d.days} Days (${d.reason})`;
            if(collectionName === 'tasks') desc = d.task;
            if(collectionName === 'advances') desc = `₹${d.amount} for ${d.reason}`;
            if(collectionName === 'expenses') desc = `₹${d.amount} (${d.type})`;
            if(collectionName === 'orders') desc = `${d.hospitalName} - ₹${d.amount}`;
            if(collectionName === 'leads') desc = `${d.org} (${d.city})`;
            if(collectionName === 'service_calls') desc = `${d.hospitalName} - ${d.remark}`;
            if(collectionName === 'installations') desc = `${d.product} @ ${d.hospital}`;
            if(collectionName === 'couriers') desc = `${d.courierName} (${d.docketNo})`;
            if(collectionName === 'pms_reports') desc = `${d.machine} @ ${d.hospital}`;
            if(collectionName === 'payment_dues') desc = `₹${d.amount} - Bill: ${d.billNo}`;
            if(collectionName === 'card_requests') desc = `Req ID: ${d.reqId}`; // Added basic desc for card req

            allPending.push({
                id: d.id,
                module: cfg.type,
                user: d.userName || d.to || 'Unknown',
                date: d.createdAt || d.date || '-',
                desc: desc,
                path: cfg.path,
                color: cfg.color
            });
        });
    };

    // Process all lists from Context
    processList(leaves, 'leaves', 'Pending');
    processList(tasks, 'tasks', 'Pending');
    processList(advances, 'advances', 'Pending');
    processList(expenses, 'expenses', 'Pending');
    processList(orders, 'orders', 'Pending');
    processList(installations, 'installations', 'Pending');
    processList(serviceCalls, 'service_calls', 'Open');
    processList(leads, 'leads', 'Open'); // Or whatever status means pending/open for leads
    processList(cardRequests, 'card_requests', 'Pending');
    processList(couriers, 'couriers', 'Pending');
    processList(pms, 'pms_reports', 'Pending');
    processList(dues, 'payment_dues', 'Open'); // Assuming 'dues' is the prop name for payment_dues

    // Sort by Date
    allPending.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    setData(allPending);
    setLoading(false);

  }, [leaves, expenses, advances, tasks, orders, installations, serviceCalls, leads, cardRequests, couriers, pms, dues, contextLoading]);

  const downloadExcel = () => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Pending_Approvals");
    XLSX.writeFile(wb, "Pending_Work_List.xlsx");
  };

  const formatDate = (iso) => {
      if(!iso) return "-";
      const date = new Date(iso);
      return isNaN(date.getTime()) ? iso : date.toLocaleDateString('en-GB');
  };

  return (
    <div style={{padding:"20px", fontFamily:"Arial"}}>
      <div style={headerStyle}>
        <h2 style={{margin:0, color:"#c0392b"}}>⚠️ Master Pending List ({data.length})</h2>
        <button onClick={downloadExcel} style={btnDownload}>📥 Download List</button>
      </div>

      {loading ? <Loader /> : (
        <div style={{overflowX:"auto"}}>
          <table border="1" style={tableStyle}>
            <thead>
                <tr style={{backgroundColor:"#c0392b", color:"white"}}>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Employee</th>
                    <th style={thStyle}>Details / Reason</th>
                    <th style={thStyle}>Action</th>
                </tr>
            </thead>
            <tbody>
              {data.map((row, index) => (
                <tr key={index} style={{textAlign:"center", height:"40px"}}>
                    <td style={{...tdStyle, fontWeight:"bold", color: row.color}}>{row.module}</td>
                    <td style={tdStyle}>{formatDate(row.date)}</td>
                    <td style={{...tdStyle, fontWeight:"bold"}}>{row.user}</td>
                    <td style={{...tdStyle, textAlign:"left"}}>{row.desc}</td>
                    <td style={tdStyle}>
                        <button 
                            onClick={() => navigate(row.path)} 
                            style={{...btnAction, backgroundColor: row.color}}
                        >
                            Go to Approve ➔
                        </button>
                    </td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan="5" style={{padding:"20px"}}>🎉 No Pending Work! Relax.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// STYLES
const headerStyle={backgroundColor:"#f8f9fa", padding:"15px", borderRadius:"8px", marginBottom:"20px", display:"flex", justifyContent:"space-between", alignItems:"center"};
const tableStyle={width:"100%", borderCollapse:"collapse", fontSize:"13px", whiteSpace:"nowrap"};
const thStyle={padding:"10px", border:"1px solid #ddd", backgroundColor:"#c0392b", color:"white"};
const tdStyle={padding:"8px", border:"1px solid #ddd"};
const btnDownload={backgroundColor:"#27ae60", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer", fontWeight:"bold"};
const btnAction={color:"white", border:"none", padding:"5px 10px", borderRadius:"4px", cursor:"pointer", fontSize:"11px", fontWeight:"bold"};

export default PendingApprovals;